import { Component, inject, OnInit, OnDestroy, signal, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { ButtonModule } from 'primeng/button';
import { LayoutService } from '../../service/layout.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { GlobalProgressSpinnerService } from '@/shared/services/global-progress-spinner.service';
import { finalize } from 'rxjs';
import { AuthService } from '@/core/services/auth.service';
import { UserProfileService } from '@/pages/user-profile/user-profile.service';
import { Skeleton } from 'primeng/skeleton';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationsService } from '@/pages/notifications/notifications.service';
import { NotificationsLiveService } from '@/pages/notifications/notifications-live.service';
import { NotificationItem, NotificationPriority } from '@/pages/notifications/data-access/notifications.model';
import { formatMinThresholdNotificationMessage } from '@/pages/notifications/data-access/min-threshold-notification.util';
import { RequestDealDialogComponent } from '@/shared/ui-components/request-deal-dialog/request-deal-dialog.component';
import { ContactRmDialogComponent } from '@/shared/ui-components/contact-rm-dialog/contact-rm-dialog.component';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { PopoverComponent } from '@/shared/ui-components/popover/popover.component';
import { PopoverItem } from '@/shared/models/popover-item.model';
import { AvatarComponent } from '@/shared/ui-components/avatar/avatar.component';
import { NotificationItemComponent, NotificationItemData } from '@/layout/component/notification-item/notification-item.component';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Role } from '@/core/models/auth.interface';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [
        RouterModule,
        CommonModule,
        StyleClassModule,
        ButtonModule,
        AngularSvgIconModule,
        Skeleton,
        TranslateModule,
        RequestDealDialogComponent,
        ContactRmDialogComponent,
        ButtonComponent,
        PopoverComponent,
        AvatarComponent,
        NotificationItemComponent,
        OverlayBadgeModule
    ],
    templateUrl: './app.topbar.html'
})
export class AppTopbar implements OnInit, OnDestroy {
    public showRequestDealDialog = signal<boolean>(false);
    public readonly _layoutService = inject(LayoutService);
    public readonly _userProfileService = inject(UserProfileService);
    public readonly _notificationsService = inject(NotificationsService);
    private readonly _notificationsLiveService = inject(NotificationsLiveService);
    private readonly _router = inject(Router);
    private readonly _globalProgressSpinnerService = inject(GlobalProgressSpinnerService);
    private readonly _authService = inject(AuthService);
    public showContactRmDialog = signal<boolean>(false);

    @ViewChild('notificationPopover') notificationPopover!: PopoverComponent;
    public ngOnInit(): void {
        this._userProfileService.getUserProfile();
    }

    public onBellClick(event: Event): void {
        this.notificationPopover?.toggle(event);
        // Refresh summary when user opens the popover.
        this._notificationsService.getNotificationsSummary();
        // Ask for browser notification permission (bell click is a valid user gesture).
        this._notificationsLiveService.requestBrowserPermission();
    }

    public toggleDarkMode(): void {
        this._layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }

    public showProfilePage(): void {
        this._router.navigateByUrl('/user-profile');
    }

    public showSettingsPage(): void {
        this._router.navigateByUrl('/account-settings');
    }

    public onLogOut(): void {
        this._globalProgressSpinnerService.showLoader();

        this._authService
            .logout()
            .pipe(finalize(() => this._globalProgressSpinnerService.hideLoader()))
            .subscribe({
                next: (res) => {
                    this._userProfileService.clearProfileState();
                    this._router.navigate(['/auth/login']);
                }
            });
    }

    public redirectToDashboard(): void {
        const role = this._userProfileService.userProfileData()?.role!;
        const dashboard = this._authService.getDashboard(role);
        this._router.navigateByUrl(dashboard);
    }

    public shouldShowBackButton(): boolean {
        const currentUrl = this._router.url;
        const role = this._userProfileService.userProfileData()?.role;

        if (!role) {
            return false;
        }

        const dashboard = this._authService.getDashboard(role);
        // Remove leading slash for comparison
        const dashboardPath = dashboard.startsWith('/') ? dashboard.substring(1) : dashboard;
        const currentPath = currentUrl.startsWith('/') ? currentUrl.substring(1) : currentUrl;

        // Show back button if not on dashboard route
        return !currentPath.startsWith(dashboardPath);
    }

    public shouldShowRequestSpecificDealButton(): boolean {

        const role = this._userProfileService.userProfileData()?.role;
        if (role !== Role.CLIENT) {
            return false;
        }

        const currentUrl = this._router.url.split('?')[0];
        const allowedRoutes = [
            '/client/dashboard',
            '/client/deals',
            '/client/deals/live',
            '/client/deals/expressed-interest',
            '/client/deals/closing',
            '/client/deals/completed',
            '/client/deals/declined',
            '/client/deals/missed',
            '/client/deals/withdrawn'
        ];

        return allowedRoutes.includes(currentUrl);
    }

    public onRequestSpecificDeal(): void {        this.showRequestDealDialog.set(true);
    }

    public onRequestDealSuccess(): void {
        setTimeout(() => {
            this.showRequestDealDialog.set(false);
        }, 1200);
    }

    public onContactRM(): void {
        this.showContactRmDialog.set(true);
    }

    public onContactRmSuccess(): void {
        setTimeout(() => {
            this.showContactRmDialog.set(false);
        }, 1200);
    }

    /**
     * Get notifications from API
     */
    public getNotifications(): NotificationItem[] {
        return this._notificationsService.getRecentNotifications();
    }

    /**
     * Map API NotificationItem to UI NotificationItemData
     */
    public mapNotificationToUI(item: NotificationItem): NotificationItemData {
        return {
            id: item.id,
            title: item.title,
            message: formatMinThresholdNotificationMessage(item.title, item.message),
            type: this.mapPriorityToType(item.priority)
            // actionButton: item.actionUrl ? {
            //     label: 'View Details',
            //     onClick: () => {
            //         if (this.notificationPopover) {
            //             this.notificationPopover.hide();
            //         }
            //         // TODO: check it
            //         this._router.navigateByUrl(item.actionUrl ?? '/notifications');
            //     }
            // } : undefined
        };
    }

    /**
     * Map notification priority to UI type
     */
    private mapPriorityToType(priority: number): 'success' | 'info' | 'warning' | 'danger' {
        switch (priority) {
            case NotificationPriority.CRITICAL:
                return 'danger';
            case NotificationPriority.HIGH:
                return 'warning';
            case NotificationPriority.MEDIUM:
                return 'success';
            default:
                return 'info';
        }
    }

    /**
     * Get mapped notifications for UI display
     */
    public getMappedNotifications(): NotificationItemData[] {
        const apiNotifications = this.getNotifications();
        return apiNotifications.map((item) => this.mapNotificationToUI(item));
    }

    /**
     * Show "Mark all as read" only when any notification is unread (status === 0).
     */
    public hasUnreadStatusZero(): boolean {
        return this.getNotifications().some((n) => n?.status === 0);
    }

    /**
     * Handle notification item click
     */
    public onNotificationClick(notification: NotificationItemData): void {
        if (this.notificationPopover) {
            this.notificationPopover.hide();
        }
        this._router.navigateByUrl('/notifications');
    }

    /**
     * Handle notification dismiss
     */
    public onNotificationDismiss(notification: NotificationItemData): void {
        // Find the original notification from API
        const apiNotification = this.getNotifications().find((n) => n.id === notification.id);
        if (apiNotification) {
            // TODO: Call API to mark as dismissed when endpoint is available
            // For now, we can mark as read
            this._notificationsService.markAllNotificationsAsRead(false, [apiNotification.id], () => {
                this._notificationsService.getNotificationsSummary();
            });
        }
    }

    /**
     * Handle notification action button click
     */
    public onNotificationAction(notificationId: number | string | undefined): void {
        if (this.notificationPopover) {
            this.notificationPopover.hide();
        }
        this._router.navigateByUrl('/notifications');
    }

    /**
     * Mark all notifications as read
     */
    public markAllAsRead(): void {
        const notifications = this.getNotifications();
        const unreadIds = notifications.filter((n) => !n.isRead).map((n) => n.id);

        if (unreadIds.length > 0) {
            this._notificationsService.markAllNotificationsAsRead(true, [], () => {
                this._notificationsService.getNotificationsSummary();
            });
        }
    }

    public viewAllNotifications(): void {
        if (this.notificationPopover) {
            this.notificationPopover.hide();
        }
        this._router.navigateByUrl('/notifications');
    }

    public getUnreadCount(): number {
        return this._notificationsService.getUnreadCount();
    }

    public getHighPriorityCount(): number {
        return this._notificationsService.getHighPriorityCount();
    }

    public getCriticalCount(): number {
        return this._notificationsService.getCriticalCount();
    }

    /**
     * Get user initials from name
     */
    public getInitials(): string | undefined {
        const name = this._userProfileService.userProfileData()?.name;
        if (!name) {
            return undefined;
        }
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }

    /**
     * Get menu items for the profile popover
     */
    public getProfileMenuItems(): PopoverItem[] {
        return [
            {
                icon: '/icons/user.svg',
                label: 'My Profile',
                action: () => this.showProfilePage()
            },
            // {
            //     icon: '/icons/user.svg',
            //     label: 'Settings',
            //     action: () => this.showSettingsPage()
            // },
            {
                icon: '/icons/log-out.svg',
                label: 'Log out',
                action: () => this.onLogOut()
            }
        ];
    }

    public ngOnDestroy(): void {}
}
